import React, { useState } from 'react';
import SkillConfidenceSlider from './SkillConfidenceSlider';
import { Plus, X } from 'lucide-react';

const COMMON_SKILLS = [
  'Python', 'JavaScript', 'Java', 'C++', 'SQL', 'React', 'Node.js',
  'Machine Learning', 'Data Science', 'Cloud Computing', 'DevOps',
  'UI/UX Design', 'Project Management', 'Communication', 'Leadership'
];

const SkillConfidenceSection = ({ skillConfidence = {}, onChange, disabled = false }) => {
  const [newSkill, setNewSkill] = useState('');
  const [showAddSkill, setShowAddSkill] = useState(false);

  const handleSkillChange = (skill, value) => {
    const updated = { ...skillConfidence, [skill]: value };
    onChange(updated);
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !skillConfidence[newSkill.trim()]) {
      handleSkillChange(newSkill.trim(), 0);
      setNewSkill('');
      setShowAddSkill(false);
    }
  };

  const handleRemoveSkill = (skill) => {
    const updated = { ...skillConfidence };
    delete updated[skill];
    onChange(updated);
  };

  const allSkills = [
    ...COMMON_SKILLS.filter(skill => skillConfidence.hasOwnProperty(skill)),
    ...Object.keys(skillConfidence).filter(skill => !COMMON_SKILLS.includes(skill))
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Skill Confidence (Self-Rated)</h4>
          <p className="text-xs text-gray-500">Rate your confidence level for each skill (0-5)</p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => setShowAddSkill(!showAddSkill)}
            className="flex items-center gap-1 px-3 py-1 text-xs text-primary-600 hover:bg-primary-50 rounded"
          >
            <Plus className="h-4 w-4" />
            Add Skill
          </button>
        )}
      </div>

      {showAddSkill && !disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder="Enter skill name"
            className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
          />
          <button
            type="button"
            onClick={handleAddSkill}
            className="px-4 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAddSkill(false);
              setNewSkill('');
            }}
            className="px-4 py-2 border border-gray-200 rounded text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {allSkills.length > 0 ? (
          allSkills.map((skill) => (
            <div key={skill} className="relative">
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              <SkillConfidenceSlider
                skill={skill}
                value={skillConfidence[skill] || 0}
                onChange={(value) => handleSkillChange(skill, value)}
                disabled={disabled}
              />
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No skills added yet. Click "Add Skill" to get started.
          </div>
        )}
      </div>

      {!disabled && COMMON_SKILLS.filter(skill => !skillConfidence.hasOwnProperty(skill)).length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Quick add common skills:</p>
          <div className="flex flex-wrap gap-2">
            {COMMON_SKILLS.filter(skill => !skillConfidence.hasOwnProperty(skill)).map((skill) => (
              <button
                key={skill}
                type="button"
                onClick={() => handleSkillChange(skill, 0)}
                className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
              >
                + {skill}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillConfidenceSection;

